import React, { useState } from 'react';
import { PageHeader } from '@components/layout/Breadcrumb';
import SectionHeader from '@components/common/SectionHeader';
import Card from '@components/common/Card';
import { FiX, FiChevronLeft, FiChevronRight, FiImage } from 'react-icons/fi';

/**
 * Gallery Page Component
 * Photo gallery with categories and lightbox
 */
const Gallery = () => {
  const breadcrumbs = [{ label: 'Gallery', path: '/gallery' }];
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Gallery categories
  const categories = [
    { id: 'all', name: 'All Photos', count: 12 },
    { id: 'facilities', name: 'Facilities', count: 4 },
    { id: 'products', name: 'Products', count: 3 },
    { id: 'operations', name: 'Operations', count: 3 },
    { id: 'events', name: 'Events', count: 2 },
  ];

  // Gallery images
  const images = [
    {
      id: 1,
      src: '/assets/images/facilities/headquarters.jpg',
      alt: 'Bukawa Headquarters Building',
      category: 'facilities',
      title: 'Our Headquarters',
      description: 'Main office in Nakisenyi Village, Iganga District',
    },
    {
      id: 2,
      src: '/assets/images/facilities/warehouse.jpg',
      alt: 'Warehouse Facility',
      category: 'facilities',
      title: 'Storage Facility',
      description: 'Modern warehouse for maize storage',
    },
    {
      id: 3,
      src: '/assets/images/facilities/truck-loading.jpg',
      alt: 'Truck Loading Operations',
      category: 'operations',
      title: 'Loading Operations',
      description: 'Maize loading for delivery',
    },
    {
      id: 4,
      src: '/assets/images/facilities/compound.jpg',
      alt: 'Company Compound',
      category: 'facilities',
      title: 'Compound Overview',
      description: 'Our main compound facilities',
    },
    {
      id: 5,
      src: '/assets/images/products/white-maize-flour.png',
      alt: 'Premium White Maize Flour',
      category: 'products',
      title: 'Premium Maize Flour',
      description: 'UNBS & GMP certified flour',
    },
    {
      id: 6,
      src: '/assets/images/products/white-maize-flour-packaging.png',
      alt: 'Maize Flour Packaging',
      category: 'products',
      title: 'Product Packaging',
      description: 'Our branded 50kg flour bags',
    },
    {
      id: 7,
      src: '/assets/images/logo/bukawa-logo.png',
      alt: 'Bukawa Logo',
      category: 'facilities',
      title: 'Our Brand',
      description: 'Bukawa ACE Ltd branding',
    },
    {
      id: 8,
      src: '/assets/images/products/maize-flour-50kg.png',
      alt: 'Maize Flour 50kg Package',
      category: 'products',
      title: '50kg Flour Package',
      description: 'Detailed packaging specifications',
    },
    // Placeholder images for remaining slots
    {
      id: 9,
      src: '/assets/images/facilities/headquarters.jpg',
      alt: 'Field Operations',
      category: 'operations',
      title: 'Field Activities',
      description: 'Working with our member farmers',
    },
    {
      id: 10,
      src: '/assets/images/facilities/compound.jpg',
      alt: 'Processing Facility',
      category: 'operations',
      title: 'Processing Plant',
      description: 'Maize milling operations',
    },
    {
      id: 11,
      src: '/assets/images/facilities/headquarters.jpg',
      alt: 'Training Session',
      category: 'events',
      title: 'Farmer Training',
      description: 'Agricultural extension workshop',
    },
    {
      id: 12,
      src: '/assets/images/facilities/warehouse.jpg',
      alt: 'Annual Meeting',
      category: 'events',
      title: 'Members Meeting',
      description: 'Annual general meeting 2024',
    },
  ];

  // Filter images by category
  const filteredImages =
    activeCategory === 'all'
      ? images
      : images.filter((img) => img.category === activeCategory);

  // Open lightbox
  const openLightbox = (image, index) => {
    setLightboxImage(image);
    setLightboxIndex(index);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxImage(null);
  };

  // Navigate lightbox
  const navigateLightbox = (direction) => {
    const newIndex =
      direction === 'next'
        ? (lightboxIndex + 1) % filteredImages.length
        : (lightboxIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredImages[newIndex]);
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex]);

  return (
    <div className="gallery-page">
      {/* Page Header */}
      <PageHeader
        title="Photo Gallery"
        description="Explore our facilities, products, and activities through our photo collection"
        breadcrumbs={breadcrumbs}
      />

      {/* Gallery Section */}
      <section className="section bg-white">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`
                  px-6 py-3 rounded-lg font-medium transition-all
                  ${
                    activeCategory === category.id
                      ? 'bg-bukawa-primary text-white shadow-md'
                      : 'bg-bukawa-light text-bukawa-gray-700 hover:bg-bukawa-primary-50'
                  }
                `}
              >
                {category.name}
                <span className="ml-2 text-sm opacity-75">({category.count})</span>
              </button>
            ))}
          </div>

          {/* Image Grid */}
          {filteredImages.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image, index) => (
                <GalleryItem
                  key={image.id}
                  image={image}
                  onClick={() => openLightbox(image, index)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FiImage className="h-16 w-16 text-bukawa-gray-300 mx-auto mb-4" />
              <p className="text-bukawa-gray-600">
                No images found in this category
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="section bg-bukawa-light">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              subtitle="Visit Us"
              title="See Our Facilities in Person"
              description="We welcome visitors to our facilities. Contact us to schedule a tour and see our operations firsthand."
              className="mb-8"
            />

            <div className="grid md:grid-cols-3 gap-6">
              <Card hover className="text-center bg-white">
                <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                  Office Hours
                </h4>
                <p className="text-sm text-bukawa-gray-600">
                  Mon-Fri: 8:00 AM - 5:00 PM
                  <br />
                  Sat: 8:00 AM - 1:00 PM
                </p>
              </Card>

              <Card hover className="text-center bg-white">
                <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                  Location
                </h4>
                <p className="text-sm text-bukawa-gray-600">
                  Nakisenyi Village
                  <br />
                  Iganga District, Uganda
                </p>
              </Card>

              <Card hover className="text-center bg-white">
                <h4 className="font-semibold text-bukawa-gray-900 mb-2">
                  Contact
                </h4>
                <p className="text-sm text-bukawa-gray-600">
                  +256 786 142168
                  <br />
                  bukawaace@gmail.com
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox
          image={lightboxImage}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox('prev')}
          onNext={() => navigateLightbox('next')}
          current={lightboxIndex + 1}
          total={filteredImages.length}
        />
      )}
    </div>
  );
};

/**
 * GalleryItem Component
 * Individual gallery image card
 */
const GalleryItem = ({ image, onClick }) => {
  return (
    <Card
      className="group cursor-pointer overflow-hidden p-0 hover:shadow-large transition-all duration-300"
      onClick={onClick}
    >
      {/* Image Container */}
      <div className="relative overflow-hidden aspect-square bg-bukawa-gray-100">
        <img
          src={image.src}
          alt={image.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-bukawa-primary-900/0 group-hover:bg-bukawa-primary-900/70 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0">
            <FiImage className="h-12 w-12 text-white mb-2" />
            <p className="text-white text-sm font-medium">View Image</p>
          </div>
        </div>
      </div>

      {/* Image Info */}
      <div className="p-4 bg-white">
        <h4 className="font-semibold text-bukawa-gray-900 mb-1 line-clamp-1">
          {image.title}
        </h4>
        <p className="text-sm text-bukawa-gray-600 line-clamp-2">
          {image.description}
        </p>
      </div>
    </Card>
  );
};

/**
 * Lightbox Component
 * Full-screen image viewer
 */
const Lightbox = ({ image, onClose, onPrev, onNext, current, total }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
        aria-label="Close"
      >
        <FiX className="h-6 w-6" />
      </button>

      {/* Navigation Buttons */}
      <button
        onClick={onPrev}
        className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
        aria-label="Previous"
      >
        <FiChevronLeft className="h-8 w-8" />
      </button>

      <button
        onClick={onNext}
        className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
        aria-label="Next"
      >
        <FiChevronRight className="h-8 w-8" />
      </button>

      {/* Image Container */}
      <div
        className="max-w-7xl max-h-[90vh] w-full px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt}
          className="w-full h-full object-contain"
        />
        
        {/* Image Info */}
        <div className="text-center mt-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            {image.title}
          </h3>
          <p className="text-white/80 mb-4">{image.description}</p>
          <p className="text-white/60 text-sm">
            Image {current} of {total}
          </p>
        </div>
      </div>

      {/* Overlay Click to Close */}
      <div
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default Gallery;